/**
 * Represents a user's role assignment, including the role key, display name, owning app key, and full role key.
 */
export interface UserRoleVm {
  roleName: string;
  displayName: string;
  appKey: string;
  fullRoleKey: string;
}

/**
 * Represents a user's permission assignment, including the permission key and its owning app key.
 */
export interface UserPermissionVm {
  permissionKey: string;
  appKey: string;
}

/**
 * Represents field group access permissions for protected fields.
 */
export interface ProtectedFieldGroupAccessVm {
  groupKey: string;
  canView: boolean;
}

/**
 * Represents user security information including app user ID, full name, email, apps, roles, permissions, and optional simulation state.
 */
export interface UserSecurityVm {
  appUserId: number;
  fullName: string;
  email: string;
  apps: string[];
  roles: UserRoleVm[];
  permissions: UserPermissionVm[];
  isSimulating?: boolean;
  realUserEmail?: string;
  realUserFullName?: string;
  protectedFieldGroups?: ProtectedFieldGroupAccessVm[];
  dataScopes?: CurrentUserDataScopeVm[];
  isUnrestricted?: boolean;
  allowedPropertyCount?: number | null;
  /**
   * State/province VALUES already normalized to full ArcGIS `POLITICAL_DIVISION` names (e.g.
   * "Illinois", not the SQL-side "IL" code — `dbo.Property.StateProvince` and ArcGIS's
   * `POLITICAL_DIVISION` store the same state in opposite formats), used to filter the Properties
   * portfolio map, or `null`/absent when no filter can safely be derived (treat as "show everything").
   *
   * ⚠️ NOT A SECURITY BOUNDARY — a client-side UX convenience only. The portfolio map is backed by
   * ArcGIS FeatureLayers, not `dbo.Property`, and has no server-enforced row scoping yet (spec §3,
   * §17 Q14). See `MapStateProvinceFilter` on the backend `UserSecurityVm` for the full derivation
   * rules (conservative — falls back to `null` whenever a scope can't be safely approximated by
   * state alone, e.g. Custom scopes or scopes without a `Property.StateProvince` "In" rule).
   */
  mapStateProvinceFilter?: string[] | null;
}

/** Display-only summary of an active DataScope assigned to the current user. */
export interface CurrentUserDataScopeVm {
  scopeKey: string;
  displayName: string;
  scopeType: string;
  rules?: UserDataScopeRuleVm[];
}

export interface UserDataScopeRuleVm {
  appKey: string;
  dimension: string;
  operator: string;
  values: string[];
  /** Human-readable values for account display; raw IDs remain in `values` for matching. */
  displayValues?: string[];
}

import {
  AiChatAdminFeedbackRequest,
  AiChatFeedbackCreatedResponse,
  AiChatFeedbackReportFilters,
  AiChatFeedbackReportResponse,
  AiChatFeedbackRequest,
  AiChatHistoryFilters,
  AiChatHistoryListResponse,
  AiChatHistoryRecord,
  AiChatRequest,
  AiChatResponse,
  AppUserPreferencesVm,
  AssetType,
  CancelPropertyRequest,
  ChecklistMilestone,
  Country,
  County,
  CreateTaskDefinitionRequest,
  CreateTaskDefinitionResponse,
  CurrencyOption,
  FxRate,
  InvestmentPhase,
  ManagementType,
  NewPropertyPriceVm,
  NewPropertyVm,
  NewTransactionPhaseNoteVm,
  PatchAppUserPreferencesVm,
  PhaseMilestoneSyncStatus,
  PhaseSnapshotDto,
  PostCreateTransactionVm,
  PrismLead,
  PrismTransaction,
  PrismTransactionChangeLog,
  Property,
  PropertyPrice,
  QualificationDecisionVm,
  ReasonCode,
  RecordTransactionExportRequest,
  ReorderTaskDefinitionsRequest,
  RepairPhaseMilestoneSyncRequest,
  ReportDetail,
  ReportInstanceListItem,
  ReportInstanceListResponse,
  SaleType,
  StateProvince,
  TaskDefinitionAdminDto,
  MyTaskDto,
  TransactionCancellation,
  TransactionExportRecord,
  TransactionHealth,
  TransactionJarvisDataVm,
  TransactionPhaseNote,
  TransactionPhaseTask,
  TransactionPhaseTaskCompletion,
  TransactionPriceType,
  TransactionsByPhase,
  TransactionsReportNotesBulk,
  TransactionTableRow,
  TransactionType,
  TriggerReportGenerationRequest,
  TriggerReportGenerationResponse,
  UpdatePropertyIdentifierRequest,
  UpdateTaskDefinitionRequest,
  UpdateTaskAssigneeRequest,
  UpdateTaskDueDateRequest,
  SetPrerequisiteOverrideRequest,
  UpsertAppUserPreferencesVm
} from '../../models/PrismTransactionModels';
import { BaseQueryFn, createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { prismTransactionEndpoints } from '../endpoints/prismTransactionEndpoints';
import getPrismBaseUrl, { prismFunctionCode } from '../utils/getBaseUrl';
import { ReleaseNotes } from '../../models/AIMModels';
import { getSimulatedRole, getSimulatedUser } from '../../../simulationService';

/**
 * A function-variable that returns the current MSAL authentication token. Initialized with a default empty-string fallback; the actual token provider is injected at runtime via `setTokenGetter`.
 */
let getToken: () => Promise<string> | string = () => '';

/**
 * The resolved base URL for the Prism investment API, produced by calling `getPrismBaseUrl()`.
 *
 * @returns A string base URL determined by environment rules (localhost, dev, or production).
 */
const baseUrl = getPrismBaseUrl();
// Remove debug console unless intentionally needed; keeping but could be removed later.
console.log({ baseUrl });
/**
 * The Azure Function code key used for local development API calls; `undefined` in production.
 *
 * @returns The function code string from `VITE_PRISM_SERVICE_FUNC_CODE`, or `undefined` in production builds.
 */
const functionCode = prismFunctionCode;
/**
 * A raw `fetchBaseQuery` configured with the Prism API base URL and an async `prepareHeaders` that fetches a MSAL access token and attaches it as a Bearer `Authorization` header.
 *
 * @returns A fetch base query function (returned by `fetchBaseQuery`) that performs HTTP requests with MSAL authentication.
 */
const rawBaseQuery = fetchBaseQuery({
  baseUrl,
  prepareHeaders: async (headers, { getState }) => {
    const token = await getToken();
    headers.set('Authorization', `Bearer ${token}`);
    const simulatedUser = getSimulatedUser();
    if (simulatedUser) {
      headers.set('X-Simulate-User', simulatedUser);
    }
    const simulatedRole = getSimulatedRole();
    if (simulatedRole) {
      headers.set('X-Simulate-Role', simulatedRole);
    }
    return headers;
  }
});

/**
 * A wrapped `baseQuery` for RTK Query that calls `rawBaseQuery` and adds error handling: injection of the Azure Function code in dev, and parsing of string error bodies into `{ message: ... }` objects.
 *
 * @returns The RTK Query result object (with `data` and/or `error`) after applying error handling transformations.
 */
const baseQueryWithErrorHandling: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  // inject function code into params for every request in dev
  if (functionCode && typeof args === 'object') {
    (args as FetchArgs).params = { code: functionCode, ...((args as FetchArgs).params || {}) };
  }

  const result = await rawBaseQuery(args, api, extraOptions);
  if (result.error && typeof result.error.data === 'string') {
    try {
      result.error.data = JSON.parse(result.error.data);
    } catch {
      result.error.data = { message: result.error.data };
    }
  }
  return result;
};

const AimApiQueryCreator = () => {
  const setTokenGetter = (getter: () => Promise<string> | string) => {
    getToken = getter;
  };

  const api = createApi({
    reducerPath: 'prismInvestmentApi',
    tagTypes: [
      'UserRoles',
      'UserAppAccess',
      'UserSecurity',
      'TransactionPriceType',
      'TransactionPrice',
      'PrismTransaction',
      'TransactionPhaseTask',
      'AssetType',
      'ManagementType',
      'TransactionType',
      'SaleType',
      'Country',
      'StateProvince',
      'County',
      'Property',
      'PropertyPrice',
      'TransactionHealth',
      'ReasonCode',
      'InvestmentPhase',
      'ChecklistMilestone',
      'TaskDefinition',
      'TransactionPhaseNote',
      'PrismLead',
      'TransactionChangeLog',
      'PhaseSnapshot',
      'TransactionExport',
      'FxRate',
      'UserPreferences',
      'AiChatFeedback',
      'Report'
    ],
    baseQuery: baseQueryWithErrorHandling,
    endpoints: (builder) => ({
      getUserAppAccess: builder.query<string[], null>({
        query: () => ({ url: `/${prismTransactionEndpoints.getUserAppAccess}` }),
        providesTags: ['UserAppAccess']
      }),
      getUserRoles: builder.query<string[], null>({
        query: () => ({ url: `/${prismTransactionEndpoints.getUserRoles}` })
      }),
      getUserSecurity: builder.query<UserSecurityVm, null>({
        query: () => ({ url: '/user/security' }),
        providesTags: ['UserSecurity'],
      }),
      getPrismTransactions: builder.query<PrismTransaction[], null>({
        query: () => ({ url: `/${prismTransactionEndpoints.getPrismTransactions}` }),
        transformResponse: (response: PrismTransaction[] | unknown) =>
          Array.isArray(response) ? response : [],
        providesTags: [{ type: 'PrismTransaction', id: 'LIST' }]
      }),
      getTransactionsReportNotesBulk: builder.query<TransactionsReportNotesBulk[], null>({
        query: () => ({ url: `/${prismTransactionEndpoints.getTransactionsReportNotesBulk}` }),
        providesTags: [{ type: 'PrismTransaction', id: 'LIST' }]
      }),
      getTransactionTasks: builder.query<TransactionPhaseTask[], number>({
        query: (transactionId) => ({
          url: `/${prismTransactionEndpoints.getTransactionTasks(transactionId)}`
        }),
        providesTags: ['TransactionPhaseTask']
      }),
      /**
       * PRM-555 — every OPEN task assigned to the calling user, across all
       * transactions they can see. Scoped server-side from the bearer token;
       * there is no userId parameter by design.
       *
       * Shares the `TransactionPhaseTask` cache tag with the per-transaction
       * task queries, so completing/reassigning a task anywhere in the app
       * refreshes the My Tasks list (and its nav badge) automatically.
       */
      getMyTasks: builder.query<MyTaskDto[], null>({
        query: () => ({ url: `/${prismTransactionEndpoints.getMyTasks}` }),
        transformResponse: (response: MyTaskDto[] | unknown) =>
          Array.isArray(response) ? response : [],
        providesTags: ['TransactionPhaseTask']
      }),
      getPrismTransctionsByInvestmentPhase: builder.query<TransactionsByPhase, null>({
        query: () => ({ url: `/${prismTransactionEndpoints.getPrismTransactionsByInvestmentPhase}` }),
        providesTags: [{ type: 'PrismTransaction', id: 'LIST' }]
      }),
      /**
       * PRM-520 — denormalized Transaction Table read projection: one row per
       * transaction visible to the caller, with gross acreage restricted to
       * in-scope properties and IRR/CoC (top-level + per-phase in
       * `phaseMetrics`) sourced from each transaction's latest phase snapshot.
       * Financial fields are redacted server-side per-caller — no client-side
       * gating needed beyond the usual `canViewFinancials` column visibility.
       */
      getTransactionTable: builder.query<TransactionTableRow[], null>({
        query: () => ({ url: `/${prismTransactionEndpoints.getTransactionTable}` }),
        transformResponse: (response: TransactionTableRow[] | unknown) =>
          Array.isArray(response) ? response : [],
        providesTags: [{ type: 'PrismTransaction', id: 'LIST' }]
      }),
      getPrismTransactionById: builder.query<PrismTransaction, number>({
        query: (transactionId) => ({
          url: `/${prismTransactionEndpoints.getPrismTransactionById(transactionId)}`
        }),
        // id-scoped tag only. The scalar-field update mutation invalidates the
        // LIST tag (below) to refresh grids/reports WITHOUT refetching this
        // detail — the post-PUT GET returns read-after-write-stale data that
        // would clobber the surgical optimistic patch (e.g. a cleared IRR/CoC
        // snapping back to its old value until a manual refresh). Other
        // mutations still invalidate the plain 'PrismTransaction' tag, which
        // covers every { type: 'PrismTransaction', ... } entry — including this
        // one — so their detail refetch behavior is unchanged.
        providesTags: (_result, _error, transactionId) => [
          { type: 'PrismTransaction', id: transactionId }
        ]
      }),
      createTransactionFolders: builder.mutation<
        { root: string; rootFolder: { id: string; webUrl?: string } },
        { transactionId: number; division: string; title: string; country?: string; state?: string; isMultiState: boolean }
      >({
        // The existing Azure Function uses GET for this write; expose it as a mutation
        // so it runs only on an explicit action and refreshes the transaction afterward.
        query: ({ transactionId, division, title, country, state, isMultiState }) => ({
          url: `/${prismTransactionEndpoints.createTransactionFolders(transactionId, division, title)}`,
          method: 'GET',
          params: { country, state, isMultiState },
        }),
        invalidatesTags: (_result, error, { transactionId }) => error ? [] : [
          { type: 'PrismTransaction', id: transactionId },
          { type: 'PrismTransaction', id: 'LIST' },
        ],
      }),
      getInvestmentPhases: builder.query<InvestmentPhase[], null>({
        query: () => ({ url: `/${prismTransactionEndpoints.getInvestmentPhases}` }),
        providesTags: ['InvestmentPhase']
      }),
      getChangeLogByTransactionId: builder.query<PrismTransactionChangeLog[], number>({
        query: (transactionId) => `/${prismTransactionEndpoints.getChangeLogByTransactionId(transactionId)}`,
        providesTags: ['TransactionChangeLog']
      }),
      getPrismTransactionPhaseSnapshots: builder.query<
        PhaseSnapshotDto[],
        { transactionId: number; includeCurrent?: boolean }
      >({
        query: ({ transactionId, includeCurrent }) => ({
          url: `/${prismTransactionEndpoints.getPrismTransactionPhaseSnapshots(transactionId)}`,
          params: includeCurrent ? { includeCurrent: true } : undefined
        }),
        transformResponse: (response: PhaseSnapshotDto[] | unknown) =>
          Array.isArray(response) ? response : [],
        providesTags: ['PhaseSnapshot']
      }),
      getTransactionExportHistory: builder.query<TransactionExportRecord[], number>({
        query: (transactionId) => ({
          url: `/${prismTransactionEndpoints.getTransactionExportHistory(transactionId)}`
        }),
        transformResponse: (response: TransactionExportRecord[] | unknown) =>
          Array.isArray(response) ? response : [],
        providesTags: ['TransactionExport']
      }),
      recordTransactionExport: builder.mutation<TransactionExportRecord, RecordTransactionExportRequest>({
        query: (body) => ({
          url: `/${prismTransactionEndpoints.recordTransactionExport}`,
          method: 'POST',
          body
        }),
        invalidatesTags: ['TransactionExport']
      }),
      getChecklistMilestones: builder.query<ChecklistMilestone[], null>({
        query: () => ({ url: `/${prismTransactionEndpoints.getChecklistMilestones}` }),
        providesTags: ['ChecklistMilestone']
      }),
      // ── Admin: Task Definitions (PRM-504) ───────────────────────────────────
      // Requires TransactionTask.EditTask. Edits apply to the task TEMPLATE —
      // every transaction, existing and future — not a single transaction.
      // getTaskDefinitions: builder.query<TaskDefinitionAdminDto[], null>({
      //   query: () => ({ url: `/${prismTransactionEndpoints.getTaskDefinitions}` }),
      //   providesTags: ['TaskDefinition'],
      //
      // }),
      getTaskDefinitions: builder.query<TaskDefinitionAdminDto[], null>({
        query: () => ({
          url: `/${prismTransactionEndpoints.getTaskDefinitions}`,
        }),
        transformResponse: (response: TaskDefinitionAdminDto[] | unknown) =>
          Array.isArray(response) ? response : [],
        providesTags: ['TaskDefinition']
      }),
      updateTaskDefinition: builder.mutation<TaskDefinitionAdminDto, { taskDefinitionId: number; data: UpdateTaskDefinitionRequest }>({
        query: ({ taskDefinitionId, data }) => ({
          url: `/${prismTransactionEndpoints.updateTaskDefinition(taskDefinitionId)}`,
          method: 'PUT',
          body: data
        }),
        invalidatesTags: ['TaskDefinition', 'TransactionPhaseTask']
      }),
      reorderTaskDefinitions: builder.mutation<void, ReorderTaskDefinitionsRequest>({
        query: (body) => ({
          url: `/${prismTransactionEndpoints.reorderTaskDefinitions}`,
          method: 'PUT',
          body
        }),
        invalidatesTags: ['TaskDefinition']
      }),
      createTaskDefinition: builder.mutation<CreateTaskDefinitionResponse, CreateTaskDefinitionRequest>({
        query: (body) => ({
          url: `/${prismTransactionEndpoints.createTaskDefinition}`,
          method: 'POST',
          body
        }),
        invalidatesTags: ['TaskDefinition', 'TransactionPhaseTask']
      }),
      // ── Phase / Milestone Sync Repair (PRM-504) ─────────────────────────────
      getPhaseMilestoneSyncStatus: builder.query<PhaseMilestoneSyncStatus, number>({
        query: (transactionId) => ({
          url: `/${prismTransactionEndpoints.getPhaseMilestoneSyncStatus(transactionId)}`
        }),
        providesTags: ['PrismTransaction']
      }),
      repairPhaseMilestoneSync: builder.mutation<
        PrismTransaction,
        { transactionId: number; data: RepairPhaseMilestoneSyncRequest }
      >({
        query: ({ transactionId, data }) => ({
          url: `/${prismTransactionEndpoints.repairPhaseMilestoneSync(transactionId)}`,
          method: 'POST',
          body: data
        }),
        invalidatesTags: ['PrismTransaction', 'TransactionPhaseTask', 'ChecklistMilestone', 'TransactionChangeLog']
      }),
      getAssetTypes: builder.query<AssetType[], null>({
        query: () => ({ url: `/${prismTransactionEndpoints.getAssetTypes}` }),
        providesTags: ['AssetType']
      }),
      getManagementTypes: builder.query<ManagementType[], null>({
        query: () => ({ url: `/${prismTransactionEndpoints.getManagementTypes}` }),
        providesTags: ['ManagementType']
      }),
      getTransactionTypes: builder.query<TransactionType[], null>({
        query: () => ({ url: `/${prismTransactionEndpoints.getTransactionTypes}` }),
        providesTags: ['TransactionType']
      }),
      getSaleTypes: builder.query<SaleType[], null>({
        query: () => ({ url: `/${prismTransactionEndpoints.getSaleTypes}` }),
        providesTags: ['SaleType']
      }),
      getCountries: builder.query<Country[], null>({
        query: () => ({ url: `/${prismTransactionEndpoints.getCountries}` }),
        transformResponse: (response: Country[] | unknown) =>
          Array.isArray(response) ? response : [],
        providesTags: ['Country']
      }),
      getStateProvinces: builder.query<StateProvince[], null>({
        query: () => ({ url: `/${prismTransactionEndpoints.getStateProvinces}` }),
        transformResponse: (response: StateProvince[] | unknown) =>
          Array.isArray(response) ? response : [],
        providesTags: ['StateProvince']
      }),
      getStateProvincesByCountryId: builder.query<StateProvince[], number>({
        query: (countryId) => ({
          url: `/${prismTransactionEndpoints.getStateProvincesByCountryId(countryId)}`
        }),
        transformResponse: (response: StateProvince[] | unknown) =>
          Array.isArray(response) ? response : [],
        providesTags: ['StateProvince']
      }),
      getCountiesByStateProvinceId: builder.query<County[], number>({
        query: (stateProvinceId) => ({
          url: `/${prismTransactionEndpoints.getCountiesByStateProvinceId(stateProvinceId)}`
        }),
        transformResponse: (response: County[] | unknown) =>
          Array.isArray(response) ? response : [],
        providesTags: ['County']
      }),
      getTransactionHealths: builder.query<TransactionHealth[], null>({
        query: () => ({ url: `/${prismTransactionEndpoints.getTransactionHealths}` }),
        providesTags: ['TransactionHealth']
      }),
      getReasonCodes: builder.query<ReasonCode[], null>({
        query: () => ({ url: `/${prismTransactionEndpoints.getReasonCodes}` }),
        providesTags: ['ReasonCode']
      }),
      getTransactionPhaseNotesByTransactionId: builder.query<
        TransactionPhaseNote[],
        { transactionId: number }
      >({
        query: ({ transactionId }) => ({
          url: `/${prismTransactionEndpoints.getTransactionPhaseNotesByTransactionId}`,
          params: { transactionId }
        }),
        providesTags: ['TransactionPhaseNote']
      }),
      getTransactionPhaseNotesByTransactionIdAndInvestmentPhaseId: builder.query<
        TransactionPhaseNote[],
        { transactionId: number; investmentPhaseId: number }
      >({
        query: ({ transactionId, investmentPhaseId }) => ({
          url: `/${prismTransactionEndpoints.getTransactionPhaseNotesByTransactionIdAndInvestmentPhaseId}`,
          params: { transactionId, investmentPhaseId }
        }),
        providesTags: ['TransactionPhaseNote']
      }),
      getPrismLeads: builder.query<PrismLead[], null>({
        query: () => ({ url: `/${prismTransactionEndpoints.getPrismLeads}` }),
        providesTags: ['PrismLead']
      }),
      addPrismTransaction: builder.mutation<PrismTransaction, PostCreateTransactionVm>({
        query: (payload) => ({
          url: `/${prismTransactionEndpoints.postPrismTransaction}`,
          method: 'POST',
          body: payload
        }),
        invalidatesTags: ['PrismTransaction', 'TransactionChangeLog']
      }),
      updatePrismTransaction: builder.mutation<PrismTransaction, PrismTransaction>({
        query: (payload) => ({
          url: `/${prismTransactionEndpoints.putPrismTransaction}`,
          method: 'PUT',
          body: payload
        }),
        // SURGICAL optimistic update. The backend returns read-after-write-stale
        // data on the GET that fires right after this PUT, so an invalidation-
        // driven refetch of the detail would briefly restore pre-save values —
        // most visibly a cleared IRR/CoC snapping back to its old number until a
        // manual refresh. To avoid that we:
        //   1) patch ONLY the transaction-level scalar fields in the detail cache
        //      (never nested `properties`/`transactionPhaseData`, so property
        //      prices etc. are untouched), and
        //   2) invalidate the plain 'PrismTransaction' tag (list/grid/report
        //      views) but NOT this transaction's id-scoped detail tag, so the
        //      on-screen overview keeps the patch instead of a stale refetch.
        async onQueryStarted(payload, { dispatch, queryFulfilled }) {
          const anyPayload = payload as unknown as { id?: number; data?: PrismTransaction } & Partial<PrismTransaction>;
          const updated = (anyPayload?.data ?? anyPayload) as Partial<PrismTransaction> | undefined;
          const transactionId = anyPayload?.id ?? (updated as PrismTransaction | undefined)?.id;
          if (transactionId == null || !updated) return;

          // Only these top-level scalar fields are editable via the transaction
          // form. Restricting the patch to them guarantees nested data (esp.
          // `properties` and their prices) is never overwritten.
          const SCALAR_FIELDS: (keyof PrismTransaction)[] = [
            'title', 'description', 'seller',
            'internalContactName', 'contactName', 'contactPhone', 'contactEmail',
            'assetTypeId', 'managementTypeId', 'saleTypeId', 'transactionTypeId',
            'country', 'stateProvince', 'county',
            'irr', 'coc', 'askingPrice',
            'primaryCurrency', 'areaUnit',
            'estCompletion', 'bidDate', 'closingDate',
            'eitDate', 'dueDiligenceDeadline', 'boardApprovalDeadline'
          ];

          const applyPatch = (draft: PrismTransaction, source: Partial<PrismTransaction>) => {
            for (const key of SCALAR_FIELDS) {
              if (Object.prototype.hasOwnProperty.call(source, key)) {
                // Cast through unknown: keys are a known subset of PrismTransaction.
                (draft as unknown as Record<string, unknown>)[key as string] =
                  (source as unknown as Record<string, unknown>)[key as string];
              }
            }
          };

          const patchResult = dispatch(
            api.util.updateQueryData('getPrismTransactionById', Number(transactionId), (draft) => {
              applyPatch(draft, updated);
            })
          );

          try {
            const { data: putResponse } = await queryFulfilled;
            // Reconcile the same scalar fields with the server's authoritative
            // response, if it returned the updated transaction.
            if (putResponse && typeof putResponse === 'object' && (putResponse as PrismTransaction).id != null) {
              dispatch(
                api.util.updateQueryData('getPrismTransactionById', Number(transactionId), (draft) => {
                  applyPatch(draft, putResponse as PrismTransaction);
                })
              );
            }
          } catch {
            // Save failed — undo the optimistic patch so the UI reflects reality.
            patchResult.undo();
          }
        },
        // Invalidate list/grid/report views (LIST tag) + change log, but NOT the
        // id-scoped detail tag — see the comment above.
        invalidatesTags: [{ type: 'PrismTransaction', id: 'LIST' }, 'TransactionChangeLog']
      }),
      updatePhaseSnapshot: builder.mutation<PhaseSnapshotDto, PhaseSnapshotDto>({
        query: (payload) => ({
          url: `/${prismTransactionEndpoints.putPrismTransactionPhaseSnapshot}`,
          method: 'PUT',
          body: payload
        }),
        invalidatesTags: ['PrismTransaction', 'TransactionChangeLog', 'PhaseSnapshot']
      }),
      cancelPrismTransaction: builder.mutation<void, number>({
        query: (transactionId) => ({
          url: `/${prismTransactionEndpoints.cancelPrismTransaction(transactionId)}`,
          method: 'POST'
        }),
        invalidatesTags: ['PrismTransaction', 'TransactionChangeLog']
      }),
      clonePrismTransaction: builder.mutation<PrismTransaction, {
        transactionId: number;
        details: {
          startingInvestmentPhaseId: number,
          startingChecklistMilestoneId: number
        }
      }>({
        query: (payload) => ({
          url: `/${prismTransactionEndpoints.clonePrismTransaction(payload.transactionId)}`,
          method: 'POST',
          body: payload.details
        }),
        invalidatesTags: ['PrismTransaction', 'TransactionChangeLog']
      }),
      mapProperty: builder.mutation<void, number>({
        query: (propertyId) => ({
          url: `/${prismTransactionEndpoints.mapProperty(propertyId)}`,
          method: 'POST'
        }),
        invalidatesTags: ['PrismTransaction', 'Property', 'TransactionChangeLog']
      }),
      getPropertyByPropertyId: builder.query<Property, number>({
        query: (propertyId) => ({
          url: `/${prismTransactionEndpoints.getPropertyByPropertyId(propertyId)}`
        }),
        providesTags: ['Property']
      }),
      getPropertiesByTransactionId: builder.query<Property[], number>({
        query: (transactionId) => ({
          url: `/${prismTransactionEndpoints.getPropertiesByTransactionId(transactionId)}`
        }),
        providesTags: ['Property']
      }),
      addProperty: builder.mutation<Property, NewPropertyVm & { prismTransactionId: number }>({
        query: (payload) => ({
          url: `/${prismTransactionEndpoints.postProperty(payload.prismTransactionId)}`,
          method: 'POST',
          body: payload
        }),
        invalidatesTags: ['Property', 'PrismTransaction', 'TransactionChangeLog']
      }),
      updateProperty: builder.mutation<Property, Property>({
        query: (payload) => ({
          url: `/${prismTransactionEndpoints.putProperty}`,
          method: 'PUT',
          body: payload
        }),
        invalidatesTags: ['Property', 'PrismTransaction', 'TransactionChangeLog']
      }),
      updatePropertyIdentifier: builder.mutation<Property, UpdatePropertyIdentifierRequest>({
        query: (payload) => ({
          url: `/${prismTransactionEndpoints.updatePropertyIdentifier}`,
          method: 'PUT',
          body: payload
        }),
        invalidatesTags: ['Property', 'PrismTransaction', 'TransactionChangeLog']
      }),
      deleteProperty: builder.mutation<void, number>({
        query: (id) => ({
          url: `/${prismTransactionEndpoints.deleteProperty}`,
          method: 'DELETE',
          params: { id }
        }),
        invalidatesTags: ['Property', 'PrismTransaction', 'TransactionChangeLog']
      }),
      cancelProperty: builder.mutation<void, { propertyId: number; payload: CancelPropertyRequest }>({
        query: ({ propertyId, payload }) => ({
          url: `/${prismTransactionEndpoints.cancelProperty(propertyId)}`,
          method: 'POST',
          body: payload
        }),
        invalidatesTags: ['Property', 'PrismTransaction', 'TransactionChangeLog']
      }),
      reactivateProperty: builder.mutation<void, { propertyId: number; }>({
        query: ({ propertyId }) => ({
          url: `/${prismTransactionEndpoints.reactivateProperty(propertyId)}`,
          method: 'POST'
        }),
        invalidatesTags: ['Property', 'PrismTransaction']
      }),
      mapPrismTransaction: builder.mutation<void, number>({
        query: (transactionId) => ({
          url: `/${prismTransactionEndpoints.mapTransaction(transactionId)}`,
          method: 'POST'
        }),
        invalidatesTags: ['PrismTransaction', 'TransactionChangeLog']
      }),
      markInvestmentPhaseComplete: builder.mutation<PrismTransaction, number>({
        query: (transactionId) => ({
          url: `/${prismTransactionEndpoints.markInvestmentPhaseComplete(transactionId)}`,
          method: 'POST'
        }),
        invalidatesTags: ['PrismTransaction', 'TransactionChangeLog']
      }),
      revertInvestmentPhase: builder.mutation<PrismTransaction, number>({
        query: (transactionId) => ({
          url: `/${prismTransactionEndpoints.revertInvestmentPhase(transactionId)}`,
          method: 'POST'
        }),
        invalidatesTags: ['PrismTransaction', 'TransactionChangeLog']
      }),
      failPrismTransaction: builder.mutation<
        PrismTransaction,
        { id: number; details: TransactionCancellation }
      >({
        query: (payload) => ({
          url: `/${prismTransactionEndpoints.failPrismTransaction(payload.id)}`,
          method: 'POST',
          body: payload.details
        }),
        invalidatesTags: ['PrismTransaction', 'TransactionChangeLog']
      }),
      rejectPrismTransaction: builder.mutation<
        PrismTransaction,
        { id: number; details: TransactionCancellation }
      >({
        query: (payload) => ({
          url: `/${prismTransactionEndpoints.rejectPrismTransaction(payload.id)}`,
          method: 'POST',
          body: payload.details
        }),
        invalidatesTags: ['PrismTransaction', 'TransactionChangeLog']
      }),
      submitPrismTransaction: builder.mutation<PrismTransaction, number>({
        query: (transactionId) => ({
          url: `/${prismTransactionEndpoints.submitPrismTransaction(transactionId)}`,
          method: 'POST'
        }),
        invalidatesTags: ['PrismTransaction', 'TransactionChangeLog']
      }),
      raisePrismTransactionHealth: builder.mutation<PrismTransaction, number>({
        query: (transactionId) => ({
          url: `/${prismTransactionEndpoints.raisePrismTransactionHealth(transactionId)}`,
          method: 'POST'
        }),
        invalidatesTags: ['PrismTransaction', 'TransactionChangeLog']
      }),
      lowerPrismTransactionHealth: builder.mutation<PrismTransaction, number>({
        query: (transactionId) => ({
          url: `/${prismTransactionEndpoints.lowerPrismTransactionHealth(transactionId)}`,
          method: 'POST'
        }),
        invalidatesTags: ['PrismTransaction', 'TransactionChangeLog']
      }),
      lockPrismTransaction: builder.mutation<void, { transactionId: number; payload: { reason?: string } }>({
        query: ({ transactionId, payload }) => ({
          url: `/${prismTransactionEndpoints.lockPrismTransaction(transactionId)}`,
          method: 'POST',
          body: payload
        }),
        invalidatesTags: ['PrismTransaction', 'TransactionChangeLog']
      }),
      unlockPrismTransaction: builder.mutation<void, number>({
        query: (transactionId) => ({
          url: `/${prismTransactionEndpoints.unlockPrismTransaction(transactionId)}`,
          method: 'POST'
        }),
        invalidatesTags: ['PrismTransaction', 'TransactionChangeLog']
      }),
      approveFinanceLead: builder.mutation<void, number>({
        query: (transactionId) => ({
          url: `/${prismTransactionEndpoints.approveFinanceLead(transactionId)}`,
          method: 'POST'
        }),
        invalidatesTags: ['PrismTransaction', 'TransactionChangeLog']
      }),
      approveTransactionLead: builder.mutation<void, number>({
        query: (transactionId) => ({
          url: `/${prismTransactionEndpoints.approveTransactionLead(transactionId)}`,
          method: 'POST'
        }),
        invalidatesTags: ['PrismTransaction', 'TransactionChangeLog']
      }),
      approveInvestmentAnalyst: builder.mutation<void, number>({
        query: (transactionId) => ({
          url: `/${prismTransactionEndpoints.approveInvestmentAnalyst(transactionId)}`,
          method: 'POST'
        }),
        invalidatesTags: ['PrismTransaction', 'TransactionChangeLog']
      }),
      qualifyOperations: builder.mutation<void, { transactionId: number; payload: QualificationDecisionVm }>({
        query: ({ transactionId, payload }) => ({
          url: `/${prismTransactionEndpoints.qualifyOperations(transactionId)}`,
          method: 'POST',
          body: payload
        }),
        invalidatesTags: ['PrismTransaction', 'TransactionChangeLog']
      }),
      qualifyCeo: builder.mutation<void, { transactionId: number; payload: QualificationDecisionVm }>({
        query: ({ transactionId, payload }) => ({
          url: `/${prismTransactionEndpoints.qualifyCeo(transactionId)}`,
          method: 'POST',
          body: payload
        }),
        invalidatesTags: ['PrismTransaction', 'TransactionChangeLog']
      }),
      declineOperations: builder.mutation<void, { transactionId: number; payload: QualificationDecisionVm }>({
        query: ({ transactionId, payload }) => ({
          url: `/${prismTransactionEndpoints.declineOperations(transactionId)}`,
          method: 'POST',
          body: payload
        }),
        invalidatesTags: ['PrismTransaction', 'TransactionChangeLog']
      }),
      declineCeo: builder.mutation<void, { transactionId: number; payload: QualificationDecisionVm }>({
        query: ({ transactionId, payload }) => ({
          url: `/${prismTransactionEndpoints.declineCeo(transactionId)}`,
          method: 'POST',
          body: payload
        }),
        invalidatesTags: ['PrismTransaction', 'TransactionChangeLog']
      }),
      addTransactionJarvisData: builder.mutation<
        PrismTransaction,
        { transactionId: number; payload: TransactionJarvisDataVm }
      >({
        query: ({ transactionId, payload }) => ({
          url: `/${prismTransactionEndpoints.postPrismTransactionJarvisData(transactionId)}`,
          method: 'POST',
          body: payload
        }),
        invalidatesTags: ['PrismTransaction', 'TransactionChangeLog']
      }),
      removeTransactionJarvisData: builder.mutation<PrismTransaction, number>({
        query: (transactionId) => ({
          url: `/${prismTransactionEndpoints.removePrismTransactionJarvisData(transactionId)}`,
          method: 'POST'
        }),
        invalidatesTags: ['PrismTransaction', 'TransactionChangeLog']
      }),
      completeAnalysis: builder.mutation<
        PrismTransaction,
        {
          transactionId: number;
          payload: TransactionPhaseTaskCompletion;
        }
        >({
        query: ({ transactionId, payload }) => ({
          url: `/${prismTransactionEndpoints.completeAnalysis(transactionId)}`,
          method: 'POST',
          body: payload
        }),
        invalidatesTags: ['PrismTransaction', 'TransactionChangeLog']
      }),
      completeDueDiligence: builder.mutation<
        PrismTransaction,
        {
          transactionId: number;
          payload: TransactionPhaseTaskCompletion;
        }
      >({
        query: ({ transactionId, payload }) => ({
          url: `/${prismTransactionEndpoints.completeDueDiligence(transactionId)}`,
          method: 'POST',
          body: payload
        }),
        invalidatesTags: ['PrismTransaction', 'TransactionChangeLog']
      }),
      completeInternalApprovals: builder.mutation<
        PrismTransaction,
        {
          transactionId: number;
          payload: TransactionPhaseTaskCompletion;
        }
      >({
        query: ({ transactionId, payload }) => ({
          url: `/${prismTransactionEndpoints.completeInternalApprovals(transactionId)}`,
          method: 'POST',
          body: payload
        }),
        invalidatesTags: ['PrismTransaction', 'TransactionChangeLog']
      }),
      completeNegotiation: builder.mutation<
        PrismTransaction,
        {
          transactionId: number;
          payload: TransactionPhaseTaskCompletion;
        }
      >({
        query: ({ transactionId, payload }) => ({
          url: `/${prismTransactionEndpoints.completeNegotiation(transactionId)}`,
          method: 'POST',
          body: payload
        }),
        invalidatesTags: ['PrismTransaction', 'TransactionChangeLog']
      }),
      completeTransactionClosing: builder.mutation<
        PrismTransaction,
        {
          transactionId: number;
          payload: TransactionPhaseTaskCompletion;
        }
      >({
        query: ({ transactionId, payload }) => ({
          url: `/${prismTransactionEndpoints.completeTransactionClosing(transactionId)}`,
          method: 'POST',
          body: payload
        }),
        invalidatesTags: ['PrismTransaction', 'TransactionChangeLog']
      }),
      completeTransactionPostClose: builder.mutation<
        PrismTransaction,
        {
          transactionId: number;
          payload: TransactionPhaseTaskCompletion;
        }
      >({
        query: ({ transactionId, payload }) => ({
          url: `/${prismTransactionEndpoints.completeTransactionPostClose(transactionId)}`,
          method: 'POST',
          body: payload
        }),
        invalidatesTags: ['PrismTransaction', 'TransactionChangeLog']
      }),
      completeTransactionPhaseTask: builder.mutation<
        void,
        {
          phaseTaskId: number;
          payload: TransactionPhaseTaskCompletion;
        }
      >({
        query: ({ phaseTaskId, payload }) => ({
          url: `/${prismTransactionEndpoints.completeTransactionPhaseTask(phaseTaskId)}`,
          method: 'POST',
          body: payload
        }),
        invalidatesTags: ['TransactionPhaseTask', 'TransactionChangeLog']
      }),
      editTransactionPhaseTaskData: builder.mutation<
        void,
        {
          phaseTaskId: number;
          payload: TransactionPhaseTaskCompletion;
        }
      >({
        query: ({ phaseTaskId, payload }) => ({
          url: `/${prismTransactionEndpoints.editTransactionPhaseTaskData(phaseTaskId)}`,
          method: 'PUT',
          body: payload
        }),
        invalidatesTags: ['TransactionPhaseTask', 'TransactionChangeLog']
      }),
      // ── PRM-555 — per-task assignee, due date & prerequisite override ─────
      // All three require TransactionTask.EditTask and return the FULL updated
      // task DTO, so callers can replace their local row from the response
      // instead of refetching the whole list.
      updateTransactionPhaseTaskAssignee: builder.mutation<
        TransactionPhaseTask,
        { phaseTaskId: number; payload: UpdateTaskAssigneeRequest }
      >({
        query: ({ phaseTaskId, payload }) => ({
          url: `/${prismTransactionEndpoints.updateTransactionPhaseTaskAssignee(phaseTaskId)}`,
          method: 'PUT',
          body: payload
        }),
        invalidatesTags: ['TransactionPhaseTask', 'TransactionChangeLog']
      }),
      updateTransactionPhaseTaskDueDate: builder.mutation<
        TransactionPhaseTask,
        { phaseTaskId: number; payload: UpdateTaskDueDateRequest }
      >({
        query: ({ phaseTaskId, payload }) => ({
          url: `/${prismTransactionEndpoints.updateTransactionPhaseTaskDueDate(phaseTaskId)}`,
          method: 'PUT',
          body: payload
        }),
        invalidatesTags: ['TransactionPhaseTask', 'TransactionChangeLog']
      }),
      setTransactionPhaseTaskPrerequisiteOverride: builder.mutation<
        TransactionPhaseTask,
        { phaseTaskId: number; payload: SetPrerequisiteOverrideRequest }
      >({
        query: ({ phaseTaskId, payload }) => ({
          url: `/${prismTransactionEndpoints.setTransactionPhaseTaskPrerequisiteOverride(phaseTaskId)}`,
          method: 'PUT',
          body: payload
        }),
        invalidatesTags: ['TransactionPhaseTask', 'TransactionChangeLog']
      }),
      addTransactionPhaseTask: builder.mutation<
        TransactionPhaseTask,
        { transactionId: number; taskDefinitionId: number }
      >({
        query: ({ transactionId, taskDefinitionId }) => ({
          url: `/${prismTransactionEndpoints.addTransactionPhaseTask(transactionId, taskDefinitionId)}`,
          method: 'POST'
        }),
        invalidatesTags: ['TransactionPhaseTask', 'TransactionChangeLog']
      }),
      removeTransactionPhaseTask: builder.mutation<
        void,
        { transactionId: number; transactionPhaseTaskId: number }
      >({
        query: ({ transactionId, transactionPhaseTaskId }) => ({
          url: `/${prismTransactionEndpoints.removeTransactionPhaseTask(transactionId, transactionPhaseTaskId)}`,
          method: 'DELETE'
        }),
        invalidatesTags: ['TransactionPhaseTask', 'TransactionChangeLog']
      }),
      transactionPriceTypes: builder.query<TransactionPriceType[], null>({
        query: () => ({ url: `/${prismTransactionEndpoints.getTransactionPriceTypes}` }),
        transformResponse: (response: TransactionPriceType[] | unknown) =>
          Array.isArray(response) ? response : [],
        providesTags: ['TransactionPriceType']
      }),
      propertyPricesByPropertyId: builder.query<PropertyPrice[], string>({
        query: (propertyId) => ({
          url: `/${prismTransactionEndpoints.getPropertyPricesByPropertyId}`,
          params: { propertyId }
        }),
        transformResponse: (response: PropertyPrice[] | unknown) =>
          Array.isArray(response) ? response : [],
        providesTags: ['PropertyPrice']
      }),
      addPropertyPrices: builder.mutation<
        PropertyPrice[],
        NewPropertyPriceVm[]
      >({
        query: (payload) => ({
          url: `/${prismTransactionEndpoints.postPropertyPrices}`,
          method: 'POST',
          body: payload
        }),
        invalidatesTags: ['PropertyPrice', 'TransactionChangeLog']
      }),
      updatePropertyPrice: builder.mutation<PropertyPrice, PropertyPrice>({
        query: (payload) => ({
          url: `/${prismTransactionEndpoints.putPropertyPrice}`,
          method: 'PUT',
          body: payload
        }),
        invalidatesTags: ['PropertyPrice', 'TransactionChangeLog']
      }),
      deletePropertyPrice: builder.mutation<void, number>({
        query: (id) => ({
          url: `/${prismTransactionEndpoints.deletePropertyPrice(id)}`,
          method: 'DELETE'
        }),
        invalidatesTags: ['PropertyPrice', 'TransactionChangeLog']
      }),
      addTransactionPhaseNote: builder.mutation<TransactionPhaseNote, NewTransactionPhaseNoteVm>({
        query: (payload) => ({
          url: `/${prismTransactionEndpoints.postTransactionPhaseNote}`,
          method: 'POST',
          body: payload
        }),
        invalidatesTags: ['TransactionPhaseNote', 'TransactionChangeLog']
      }),
      updateTransactionPhaseNote: builder.mutation<TransactionPhaseNote, TransactionPhaseNote>({
        query: (payload) => ({
          url: `/${prismTransactionEndpoints.putTransactionPhaseNote}`,
          method: 'PUT',
          body: payload
        }),
        invalidatesTags: ['TransactionPhaseNote', 'TransactionChangeLog']
      }),
      deleteTransactionPhaseNote: builder.mutation<void, number>({
        query: (id) => ({
          url: `/${prismTransactionEndpoints.deleteTransactionPhaseNote(id)}`,
          method: 'POST'
        }),
        invalidatesTags: ['TransactionPhaseNote', 'TransactionChangeLog']
      }),
      updateTransactionTeamLead: builder.mutation<
        void,
        { id: number; payload: { roleName: string; leadUserName: string; leadFullName: string } }
      >({
        query: ({ id, payload }) => ({
          url: `/${prismTransactionEndpoints.updateTransactionTeamLead(id)}`,
          method: 'POST',
          body: payload
        }),
        invalidatesTags: ['PrismTransaction', 'TransactionChangeLog']
      }),
      getCurrencies: builder.query<CurrencyOption[], null>({
        query: () => ({
          url: `/${prismTransactionEndpoints.getCurrencies}`
        }),
        transformResponse: (response: CurrencyOption[] | unknown) => {
          const list = Array.isArray(response) ? response : [];
          const hasUsd = list.some((c) => c.currency.toUpperCase() === 'USD');
          return hasUsd ? list : [...list, { currency: 'USD', symbol: '$' }];
        }
      }),
      getFxRateByCurrency: builder.query<FxRate, string>({
        query: (currency) => ({
          url: `/${prismTransactionEndpoints.getFxRateByCurrency(currency)}`
        }),
        providesTags: ['FxRate']
      }),
      getReleaseNotes: builder.query<ReleaseNotes, { app: string; version: string }>({
        query: ({ app, version }) => `/${prismTransactionEndpoints.getVersion}/${app}/${version}`
      }),
      getAppUserPreferences: builder.query<AppUserPreferencesVm | null, string>({
        query: (appValue) => ({
          url: `/${prismTransactionEndpoints.getAppUserPreferences}`,
          params: { appValue }
        }),
        // 204 No Content → treat as null
        transformResponse: (response: AppUserPreferencesVm | undefined) => response ?? null,
        providesTags: ['UserPreferences']
      }),
      upsertAppUserPreferences: builder.mutation<AppUserPreferencesVm, UpsertAppUserPreferencesVm>({
        query: (payload) => ({
          url: `/${prismTransactionEndpoints.upsertAppUserPreferences}`,
          method: 'POST',
          body: payload
        }),
        async onQueryStarted(payload, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            api.util.updateQueryData('getAppUserPreferences', payload.appValue, (draft) => {
              if (draft) {
                // Full replacement — the hook always sends the complete merged prefs object
                draft.preferences = payload.preferences;
              } else {
                return {
                  id: 0,
                  appId: 0,
                  appUserId: 0,
                  preferences: payload.preferences,
                  updatedOn: new Date().toISOString(),
                };
              }
            })
          );
          try {
            const { data } = await queryFulfilled;
            dispatch(
              api.util.updateQueryData('getAppUserPreferences', payload.appValue, () => data)
            );
          } catch {
            patchResult.undo();
          }
        },
      }),
      patchAppUserPreference: builder.mutation<void, PatchAppUserPreferencesVm>({
        query: (payload) => ({
          url: `/${prismTransactionEndpoints.patchAppUserPreferences}`,
          method: 'PATCH',
          body: payload
        }),
        async onQueryStarted(payload, { dispatch, queryFulfilled }) {
          // Optimistic update — immediate, no waiting for server
          const patchResult = dispatch(
            api.util.updateQueryData('getAppUserPreferences', payload.appValue, (draft) => {
              if (draft) {
                draft.preferences[payload.key] = payload.value;
              } else {
                return {
                  id: 0,
                  appId: 0,
                  appUserId: 0,
                  preferences: { [payload.key]: payload.value },
                  updatedOn: new Date().toISOString(),
                };
              }
            })
          );
          try {
            await queryFulfilled; // 204 — nothing to read, cache is already correct
          } catch {
            patchResult.undo(); // revert on failure
          }
        },
      }),
      refreshInvestmentData: builder.mutation<void, void>({
        query: () => ({
          url: `/${prismTransactionEndpoints.refreshInvestmentData}`,
          method: 'POST',
        }),
      }),
      postAiChat: builder.mutation<AiChatResponse, AiChatRequest>({
        query: (body) => ({
          url: '/AiChat',
          method: 'POST',
          body,
        }),
      }),
      postAiChatFeedback: builder.mutation<AiChatFeedbackCreatedResponse, AiChatFeedbackRequest>({
        query: (body) => ({
          url: '/AiChat/Feedback',
          method: 'POST',
          body,
        }),
        invalidatesTags: ['AiChatFeedback'],
      }),
      updateAiChatAdminFeedback: builder.mutation<
        void,
        { id: number; body: AiChatAdminFeedbackRequest }
      >({
        query: ({ id, body }) => ({
          url: `/AiChat/Feedback/${id}/AdminFeedback`,
          method: 'PUT',
          body,
        }),
        invalidatesTags: ['AiChatFeedback'],
      }),
      getAiChatFeedbackReport: builder.query<
        AiChatFeedbackReportResponse,
        AiChatFeedbackReportFilters
      >({
        query: (filters) => {
          const params: Record<string, string | number> = {};
          if (filters.from) params.from = filters.from;
          if (filters.to) params.to = filters.to;
          if (filters.outcome && filters.outcome.length > 0)
            params.outcome = filters.outcome.join(',');
          if (filters.rating && filters.rating !== 'Any') params.rating = filters.rating;
          if (filters.triaged && filters.triaged !== 'Any')
            params.triaged = filters.triaged === 'Yes' ? 'true' : 'false';
          if (filters.limit) params.limit = filters.limit;
          return { url: '/AiChat/FeedbackReport', params };
        },
        providesTags: ['AiChatFeedback'],
      }),
      getAiChatFeedbackReportMarkdown: builder.query<string, AiChatFeedbackReportFilters>({
        query: (filters) => {
          const params: Record<string, string | number> = { format: 'md' };
          if (filters.from) params.from = filters.from;
          if (filters.to) params.to = filters.to;
          if (filters.outcome && filters.outcome.length > 0)
            params.outcome = filters.outcome.join(',');
          if (filters.rating && filters.rating !== 'Any') params.rating = filters.rating;
          if (filters.triaged && filters.triaged !== 'Any')
            params.triaged = filters.triaged === 'Yes' ? 'true' : 'false';
          if (filters.limit) params.limit = filters.limit;
          return {
            url: '/AiChat/FeedbackReport',
            params,
            responseHandler: (response: Response) => response.text(),
          };
        },
      }),
      getAiChatHistory: builder.query<AiChatHistoryListResponse, AiChatHistoryFilters | void>({
        query: (filters) => {
          const params: Record<string, string> = {};
          if (filters) {
            if (filters.userName) params.userName = filters.userName;
            if (filters.outcome) params.outcome = filters.outcome;
            if (filters.fromDate) params.fromDate = filters.fromDate;
            if (filters.toDate) params.toDate = filters.toDate;
            if (filters.questionContains) params.questionContains = filters.questionContains;
            if (typeof filters.refusedOnly === 'boolean') params.refusedOnly = String(filters.refusedOnly);
            if (filters.page) params.page = String(filters.page);
            if (filters.pageSize) params.pageSize = String(filters.pageSize);
            if (filters.sortBy) params.sortBy = filters.sortBy;
          }
          return { url: '/AiChat/History', params };
        },
      }),
      getMyAiChatHistory: builder.query<AiChatHistoryListResponse, Omit<AiChatHistoryFilters, 'userName'> | void>({
        query: (filters) => {
          const params: Record<string, string> = {};
          if (filters) {
            if (filters.outcome) params.outcome = filters.outcome;
            if (filters.fromDate) params.fromDate = filters.fromDate;
            if (filters.toDate) params.toDate = filters.toDate;
            if (filters.questionContains) params.questionContains = filters.questionContains;
            if (typeof filters.refusedOnly === 'boolean') params.refusedOnly = String(filters.refusedOnly);
            if (filters.page) params.page = String(filters.page);
            if (filters.pageSize) params.pageSize = String(filters.pageSize);
            if (filters.sortBy) params.sortBy = filters.sortBy;
          }
          return { url: '/AiChat/History/MyHistory', params };
        },
      }),
      getAiChatHistoryById: builder.query<AiChatHistoryRecord, number>({
        query: (id) => `/AiChat/History/${id}`,
      }),
      // ─── Transaction Reports ───────────────────────────────────────────────
      getReports: builder.query<ReportInstanceListItem[], { readAll?: boolean } | void>({
        query: (arg) => ({
          url: `/${prismTransactionEndpoints.getReports}`,
          params: arg && (arg as { readAll?: boolean }).readAll ? { all: 'true' } : undefined,
        }),
        transformResponse: (response: ReportInstanceListResponse | ReportInstanceListItem[] | unknown) => {
          if (Array.isArray(response)) return response;
          const items = (response as ReportInstanceListResponse)?.items;
          return Array.isArray(items) ? items : [];
        },
        providesTags: ['Report'],
      }),
      getReportById: builder.query<ReportDetail, number>({
        query: (reportInstanceId) => ({
          url: `/${prismTransactionEndpoints.getReportById(reportInstanceId)}`,
        }),
        providesTags: ['Report'],
      }),
      // Dev-only: manually trigger report generation + email (non-prod).
      triggerReportGeneration: builder.mutation<
        TriggerReportGenerationResponse,
        TriggerReportGenerationRequest
      >({
        query: (body) => ({
          url: `/${prismTransactionEndpoints.triggerReportGeneration}`,
          method: 'POST',
          body: body ?? {},
        }),
        invalidatesTags: ['Report'],
      }),
    })
  });

  return { hooks: api, setTokenGetter };
};

/**
 * The exported Prism investment API service instance created by `AimApiQueryCreator`, whose `.hooks` property contains all RTK Query hooks (queries and mutations) for managing apps, roles, permissions, users, and usage logs.
 */
export const prismInvestmentService = AimApiQueryCreator();
export const {
  useGetReleaseNotesQuery,
  useGetUserAppAccessQuery,
  useGetUserRolesQuery,
  useGetPrismTransactionsQuery,
  useGetTransactionsReportNotesBulkQuery,
  useLazyGetTransactionTasksQuery,
  useGetMyTasksQuery,
  useGetPrismTransctionsByInvestmentPhaseQuery,
  useGetTransactionTableQuery,
  useLazyGetPrismTransactionByIdQuery,
  useGetPrismTransactionByIdQuery,
  useCreateTransactionFoldersMutation,
  useGetInvestmentPhasesQuery,
  useGetChecklistMilestonesQuery,
  useLazyGetChangeLogByTransactionIdQuery,
  useGetPrismTransactionPhaseSnapshotsQuery,
  useLazyGetPrismTransactionPhaseSnapshotsQuery,
  useGetTransactionExportHistoryQuery,
  useRecordTransactionExportMutation,
  useGetCountriesQuery,
  useGetTransactionHealthsQuery,
  useGetStateProvincesQuery,
  useLazyGetStateProvincesByCountryIdQuery,
  useLazyGetCountiesByStateProvinceIdQuery,
  useGetAssetTypesQuery,
  useGetManagementTypesQuery,
  useGetTransactionTypesQuery,
  useGetSaleTypesQuery,
  useGetReasonCodesQuery,
  useLazyGetTransactionPhaseNotesByTransactionIdQuery,
  useLazyGetTransactionPhaseNotesByTransactionIdAndInvestmentPhaseIdQuery,
  useGetPrismLeadsQuery,
  useAddPrismTransactionMutation,
  useUpdatePrismTransactionMutation,
  useUpdatePhaseSnapshotMutation,
  useCancelPrismTransactionMutation,
  useClonePrismTransactionMutation,
  useMapPropertyMutation,
  useLazyGetPropertyByPropertyIdQuery,
  useGetPropertyByPropertyIdQuery,
  useLazyGetPropertiesByTransactionIdQuery,
  useGetPropertiesByTransactionIdQuery,
  useAddPropertyMutation,
  useUpdatePropertyMutation,
  useUpdatePropertyIdentifierMutation,
  useDeletePropertyMutation,
  useCancelPropertyMutation,
  useReactivatePropertyMutation,
  useMapPrismTransactionMutation,
  useMarkInvestmentPhaseCompleteMutation,
  useRevertInvestmentPhaseMutation,
  useFailPrismTransactionMutation,
  useRejectPrismTransactionMutation,
  useSubmitPrismTransactionMutation,
  useRaisePrismTransactionHealthMutation,
  useLowerPrismTransactionHealthMutation,
  useLockPrismTransactionMutation,
  useUnlockPrismTransactionMutation,
  useApproveFinanceLeadMutation,
  useApproveInvestmentAnalystMutation,
  useApproveTransactionLeadMutation,
  useQualifyCeoMutation,
  useQualifyOperationsMutation,
  useDeclineCeoMutation,
  useDeclineOperationsMutation,
  useAddTransactionJarvisDataMutation,
  useRemoveTransactionJarvisDataMutation,
  useCompleteAnalysisMutation,
  useCompleteDueDiligenceMutation,
  useCompleteInternalApprovalsMutation,
  useCompleteNegotiationMutation,
  useCompleteTransactionClosingMutation,
  useCompleteTransactionPostCloseMutation,
  useCompleteTransactionPhaseTaskMutation,
  useEditTransactionPhaseTaskDataMutation,
  useUpdateTransactionPhaseTaskAssigneeMutation,
  useUpdateTransactionPhaseTaskDueDateMutation,
  useSetTransactionPhaseTaskPrerequisiteOverrideMutation,
  useAddTransactionPhaseTaskMutation,
  useRemoveTransactionPhaseTaskMutation,
  useTransactionPriceTypesQuery,
  useLazyPropertyPricesByPropertyIdQuery,
  useAddPropertyPricesMutation,
  useUpdatePropertyPriceMutation,
  useDeletePropertyPriceMutation,
  useAddTransactionPhaseNoteMutation,
  useUpdateTransactionPhaseNoteMutation,
  useDeleteTransactionPhaseNoteMutation,
  useGetFxRateByCurrencyQuery,
  useLazyGetFxRateByCurrencyQuery,
  useGetCurrenciesQuery,
  useLazyGetCurrenciesQuery,
  useUpdateTransactionTeamLeadMutation,
  useGetAppUserPreferencesQuery,
  useLazyGetAppUserPreferencesQuery,
  useUpsertAppUserPreferencesMutation,
  usePatchAppUserPreferenceMutation,
  useRefreshInvestmentDataMutation,
  useGetUserSecurityQuery,
  usePostAiChatMutation,
  usePostAiChatFeedbackMutation,
  useUpdateAiChatAdminFeedbackMutation,
  useGetAiChatFeedbackReportQuery,
  useLazyGetAiChatFeedbackReportQuery,
  useLazyGetAiChatFeedbackReportMarkdownQuery,
  useGetAiChatHistoryQuery,
  useLazyGetAiChatHistoryQuery,
  useGetMyAiChatHistoryQuery,
  useLazyGetMyAiChatHistoryQuery,
  useGetAiChatHistoryByIdQuery,
  useLazyGetAiChatHistoryByIdQuery,
  useGetReportsQuery,
  useGetReportByIdQuery,
  useTriggerReportGenerationMutation,
  useGetTaskDefinitionsQuery,
  useUpdateTaskDefinitionMutation,
  useReorderTaskDefinitionsMutation,
  useCreateTaskDefinitionMutation,
  useGetPhaseMilestoneSyncStatusQuery,
  useRepairPhaseMilestoneSyncMutation,
} = prismInvestmentService.hooks;
